import { useCallback } from "react";
import { addToast } from "@heroui/react";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import { Document, Packer, Paragraph, HeadingLevel, ImageRun } from "docx";
import PptxGenJS from "pptxgenjs";
import { useBrand } from "@webuild-ai/react-auth";

import { Artefact, LANGUAGE_FILE_EXTENSIONS, ArtefactType } from "@/types/artefacts";
import { ARTEFACT_CONFIG, ARTEFACT_ERRORS } from "@/config/artefacts";
import { parseTableContent, tableToCSV, tableToJSON, tableToMarkdown } from "@/utils/tableUtils";
import { DownloadFormat } from "@/utils/artefactFormatDetection";
import { convertMarkdownToDocx } from "@/utils/markdownToDocx";
import { renderMarkdownToPdf } from "@/utils/pdfRenderer";
import { convertMarkdownToPptxSlides } from "@/utils/markdownToPptx";
import { batchExportToExcel } from "@/utils/artefactExport/excelExport";
import { prepareMarkdownWithTitle } from "@/utils/artefactExport/titleUtils";
import { escapeCssSelector, sanitizeUnsupportedColors } from "@/utils/styleUtils";
import { useTheme } from "@/hooks/use-theme";
import { useUIPreferences } from "@/stores/UIPreferencesStore";
import { DEFAULT_FONT, extractPrimaryFont } from "@/utils/fontUtils";
import { logger } from "@/telemetry";

interface CopyOptions {
  includeFilename?: boolean;
  includeMetadata?: boolean;
}

/**
 * Generate filename with timestamp
 * Sanitizes filename to prevent path traversal attacks
 */
const generateFilename = (artefact: Artefact, extension: string): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
  const dateStr = timestamp[0];
  const timeStr = timestamp[1].substring(0, 8); // Remove milliseconds

  // Sanitize filename: remove path separators and special characters
  // Only allow alphanumeric, spaces, hyphens, and underscores
  const baseFilename = artefact.title
    .replace(/[/\\]/g, "") // Remove path separators
    .replace(/\.\./g, "") // Remove parent directory references
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, "_") // Replace disallowed characters with underscore
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/_+/g, "_") // Collapse multiple underscores
    .substring(0, 100); // Limit length to prevent issues

  return `${baseFilename}_${dateStr}_${timeStr}.${extension}`;
};

/**
 * Hook for common artefact actions (copy, download, refresh)
 */
export const useArtefactActions = () => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { theme } = useTheme();
  const { exportFontFamily, exportPageSize } = useUIPreferences();

  /**
   * Get the effective font family for Word exports based on user preference
   * "brand" = use brand font, otherwise use the explicitly selected font.
   * Uses extractPrimaryFont() to handle CSS font-family strings like "Raleway,sans-serif"
   * since the docx library expects a single font name, not a font stack.
   */
  const getExportFontFamily = (): string => {
    if (exportFontFamily === "brand") {
      return extractPrimaryFont(brand.fontFamily || DEFAULT_FONT);
    }

    return extractPrimaryFont(exportFontFamily);
  };

  // HTML-specific image copy function (defined first so handleCopy can reference it)
  const handleCopyAsImage = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Capturing image..."),
        description: t("Please wait while we capture the content"),
        color: "default",
      });

      try {
        // Find the iframe element by artefact ID (escape for CSS selector safety)
        const escapedId = escapeCssSelector(artefact.id);
        const iframe = document.querySelector(
          `iframe[data-artefact-id="${escapedId}"]`,
        ) as HTMLIFrameElement;

        if (!iframe) {
          throw new Error("Iframe not found for artefact");
        }

        if (!iframe.contentDocument) {
          throw new Error("Cannot access iframe content");
        }

        // Capture the iframe content as canvas with high quality
        const canvas = await html2canvas(iframe.contentDocument.body, {
          scale: 3, // High DPI for crisp images (3x device pixel ratio)
          backgroundColor: null, // Transparent background for dark mode support
          logging: false,
          useCORS: true,
          allowTaint: false,
          removeContainer: true,
          imageTimeout: 15000,
          windowWidth: iframe.contentDocument.body.scrollWidth,
          windowHeight: iframe.contentDocument.body.scrollHeight,
          // Sanitize unsupported CSS colour functions before capture
          onclone: (clonedDoc: globalThis.Document) => {
            sanitizeUnsupportedColors(clonedDoc);
          },
        });

        // Convert canvas to blob
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((result) => {
            if (result) {
              resolve(result);
            } else {
              reject(new Error("Failed to convert canvas to blob"));
            }
          }, "image/png");
        });

        // Copy image to clipboard using ClipboardItem API
        const item = new ClipboardItem({ "image/png": blob });

        await navigator.clipboard.write([item]);

        // Remove loading toast and show success
        addToast({
          title: t("Image copied!"),
          description: t("Content has been copied as an image to your clipboard"),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to copy as image", error);

        // Show error toast
        addToast({
          title: t("Copy failed"),
          description: t("Unable to copy content as image. Try downloading instead."),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleCopy = useCallback(
    async (artefact: Artefact, options: CopyOptions = {}) => {
      // Delegate HTML artefacts to image copy
      if (artefact.type === ArtefactType.HTML) {
        return handleCopyAsImage(artefact);
      }

      try {
        let contentToCopy = artefact.content;

        // Add filename header if requested
        if (options.includeFilename && artefact.title) {
          const fileExtension =
            artefact.metadata?.fileExtension ||
            LANGUAGE_FILE_EXTENSIONS[artefact.language || ""] ||
            ARTEFACT_CONFIG.fileExtensions.fallback;

          const filename = `${artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_")}.${fileExtension}`;
          const timestamp = new Date().toLocaleString();

          contentToCopy = `// File: ${filename}\n// Generated: ${timestamp}\n\n${artefact.content}`;
        }

        // Add metadata if requested
        if (options.includeMetadata) {
          const language = artefact.language || "text";
          const lineCount = artefact.metadata?.lineCount || artefact.content.split("\n").length;

          const metadataHeader = `// Language: ${language}\n// Lines: ${lineCount}\n// Copied: ${new Date().toLocaleString()}\n\n`;

          contentToCopy = metadataHeader + contentToCopy;
        }

        await navigator.clipboard.writeText(contentToCopy);

        // Show success toast notification
        const description =
          options.includeFilename || options.includeMetadata
            ? t("Content with metadata has been copied to your clipboard")
            : t("Content has been copied to your clipboard");

        addToast({
          title: t("Code copied!"),
          description,
          color: "success",
        });

        // Copy operation completed successfully
      } catch (error) {
        logger.error(`❌ ${ARTEFACT_ERRORS.copy}`, error);

        // Show error toast notification
        addToast({
          title: t("Copy failed"),
          description: t("Unable to copy content to clipboard"),
          color: "danger",
        });
      }
    },
    [handleCopyAsImage, t],
  );

  const handleDownload = useCallback(
    (artefact: Artefact) => {
      try {
        const fileExtension =
          artefact.metadata?.fileExtension ||
          LANGUAGE_FILE_EXTENSIONS[artefact.language || ""] ||
          ARTEFACT_CONFIG.fileExtensions.fallback;

        // Enhanced filename with timestamp for uniqueness
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
        const dateStr = timestamp[0];
        const timeStr = timestamp[1].substring(0, 8); // Remove milliseconds
        const baseFilename = artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
        const filename = `${baseFilename}_${dateStr}_${timeStr}.${fileExtension}`;

        const blob = new Blob([artefact.content], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Show success toast notification
        addToast({
          title: t("File downloaded!"),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });

        // Download operation completed successfully
      } catch (error) {
        logger.error(`❌ ${ARTEFACT_ERRORS.download}`, error);

        // Show error toast notification
        addToast({
          title: t("Download failed"),
          description: t("Unable to download the file"),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleDownloadAsImage = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Capturing image..."),
        description: t("Please wait while we prepare your download"),
        color: "default",
      });

      try {
        // Find the element by artefact ID (escape for CSS selector safety)
        const escapedId = escapeCssSelector(artefact.id);

        // For HTML artefacts, look for iframe; for others (TABLE, CODE, DOCUMENT), look for div
        let elementToCapture: HTMLElement | null = null;
        let captureWidth: number;
        let captureHeight: number;

        if (artefact.type === ArtefactType.HTML) {
          // HTML artefacts render in iframes
          const iframe = document.querySelector(
            `iframe[data-artefact-id="${escapedId}"]`,
          ) as HTMLIFrameElement;

          if (!iframe) {
            throw new Error("Iframe not found for artefact");
          }

          if (!iframe.contentDocument) {
            throw new Error("Cannot access iframe content");
          }

          elementToCapture = iframe.contentDocument.body;
          captureWidth = elementToCapture.scrollWidth;
          captureHeight = elementToCapture.scrollHeight;
        } else if (artefact.type === ArtefactType.TABLE) {
          // TABLE artefacts have nested containers with overflow constraints
          // We need to capture the table element directly for proper dimensions
          const container = document.querySelector(
            `[data-artefact-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!container) {
            throw new Error("Element not found for artefact");
          }

          // Find the actual table element inside the container
          const tableElement = container.querySelector("table") as HTMLElement;

          if (tableElement) {
            // Use the table's natural dimensions (not constrained by parent overflow)
            captureWidth = Math.max(tableElement.scrollWidth, tableElement.offsetWidth, 800);
            captureHeight = Math.max(tableElement.scrollHeight, tableElement.offsetHeight, 400);
          } else {
            // Fallback to container dimensions if no table found
            captureWidth = container.scrollWidth;
            captureHeight = container.scrollHeight;
          }

          elementToCapture = container;
        } else {
          // CODE, DOCUMENT artefacts render directly in the DOM
          const container = document.querySelector(
            `[data-artefact-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!container) {
            throw new Error("Element not found for artefact");
          }

          elementToCapture = container;
          captureWidth = container.scrollWidth;
          captureHeight = container.scrollHeight;
        }

        // Capture the element as canvas with high quality
        const canvas = await html2canvas(elementToCapture, {
          scale: 3, // High DPI for crisp images (3x device pixel ratio)
          backgroundColor: "#ffffff", // White background for better image quality
          logging: false,
          useCORS: true,
          allowTaint: false,
          removeContainer: true,
          imageTimeout: 15000,
          windowWidth: captureWidth,
          windowHeight: captureHeight,
          // Sanitize unsupported CSS colour functions and remove height constraints before capture
          onclone: (clonedDoc: globalThis.Document) => {
            sanitizeUnsupportedColors(clonedDoc);

            // For TABLE artefacts, remove all constraints to capture full content
            if (artefact.type === ArtefactType.TABLE) {
              // Find and remove max-height, overflow, and width constraints from the cloned element
              const clonedContainer = clonedDoc.querySelector(
                `[data-artefact-id="${escapedId}"]`,
              ) as HTMLElement;

              if (clonedContainer) {
                // Remove all constraints from the container and its children
                const allElements = clonedContainer.querySelectorAll("*");

                allElements.forEach((el) => {
                  const element = el as HTMLElement;

                  // Remove height constraints
                  element.style.maxHeight = "none";
                  element.style.height = "auto";

                  // Remove overflow constraints
                  element.style.overflow = "visible";
                  element.style.overflowX = "visible";
                  element.style.overflowY = "visible";

                  // Remove width constraints that truncate content
                  if (element.classList.contains("truncate")) {
                    element.style.maxWidth = "none";
                    element.style.overflow = "visible";
                    element.style.textOverflow = "clip";
                    element.style.whiteSpace = "normal";
                  }

                  // Ensure table cells show full content
                  if (element.tagName === "TD" || element.tagName === "TH") {
                    element.style.maxWidth = "none";
                    element.style.whiteSpace = "nowrap";
                  }
                });

                // Update the container itself
                clonedContainer.style.maxHeight = "none";
                clonedContainer.style.height = "auto";
                clonedContainer.style.overflow = "visible";
                clonedContainer.style.width = "auto";
                clonedContainer.style.minWidth = "fit-content";
              }
            }
          },
        });

        // Convert canvas to blob
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((result) => {
            if (result) {
              resolve(result);
            } else {
              reject(new Error("Failed to convert canvas to blob"));
            }
          }, "image/png");
        });

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
        const dateStr = timestamp[0];
        const timeStr = timestamp[1].substring(0, 8); // Remove milliseconds
        const baseFilename = artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
        const filename = `${baseFilename}_${dateStr}_${timeStr}.png`;

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Show success toast
        addToast({
          title: t("Image downloaded!"),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to download as image", error);

        // Show error toast
        addToast({
          title: t("Download failed"),
          description: t("Unable to download content as image"),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleRefresh = useCallback((artefactId: string) => {
    try {
      const escapedId = escapeCssSelector(artefactId);
      const iframe = document.querySelector(
        `iframe[data-artefact-id="${escapedId}"]`,
      ) as HTMLIFrameElement;

      if (iframe) {
        const currentSrc = iframe.getAttribute("srcdoc");

        iframe.setAttribute("srcdoc", "");
        setTimeout(() => {
          iframe.setAttribute("srcdoc", currentSrc || "");
        }, ARTEFACT_CONFIG.iframe.refreshDelay);
      }
    } catch (error) {
      logger.error("❌ Failed to refresh artefact", error);
    }
  }, []);

  const formatTimestamp = useCallback((timestamp: string): string => {
    const date = new Date(timestamp);

    return date.toLocaleString();
  }, []);

  // Convenience methods for different copy formats
  const handleCopyPlain = useCallback((artefact: Artefact) => handleCopy(artefact), [handleCopy]);

  const handleCopyWithFilename = useCallback(
    (artefact: Artefact) => handleCopy(artefact, { includeFilename: true }),
    [handleCopy],
  );

  const handleCopyWithMetadata = useCallback(
    (artefact: Artefact) => handleCopy(artefact, { includeMetadata: true }),
    [handleCopy],
  );

  // Table-specific export functions
  const handleCopyAsCSV = useCallback(
    async (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.TABLE) {
          throw new Error("Artefact is not a table");
        }

        const tableData = parseTableContent(artefact.content);

        if (!tableData) {
          throw new Error("Failed to parse table data");
        }

        const csvContent = tableToCSV(tableData.headers, tableData.rows);

        await navigator.clipboard.writeText(csvContent);

        addToast({
          title: t("CSV copied!"),
          description: t("Table data copied as CSV format"),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to copy as CSV", error);
        addToast({
          title: t("Copy failed"),
          description: t("Unable to copy table as CSV"),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleCopyAsJSON = useCallback(
    async (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.TABLE) {
          throw new Error("Artefact is not a table");
        }

        const tableData = parseTableContent(artefact.content);

        if (!tableData) {
          throw new Error("Failed to parse table data");
        }

        const jsonContent = tableToJSON(tableData.rows);

        await navigator.clipboard.writeText(jsonContent);

        addToast({
          title: t("JSON copied!"),
          description: t("Table data copied as JSON format"),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to copy as JSON", error);
        addToast({
          title: t("Copy failed"),
          description: t("Unable to copy table as JSON"),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleDownloadAsCSV = useCallback(
    (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.TABLE) {
          throw new Error("Artefact is not a table");
        }

        const tableData = parseTableContent(artefact.content);

        if (!tableData) {
          throw new Error("Failed to parse table data");
        }

        const csvContent = tableToCSV(tableData.headers, tableData.rows);

        // Generate smart filename based on content
        const baseFilename = artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
        const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
        const filename = `${baseFilename}_${timestamp}.csv`;

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        addToast({
          title: t("CSV downloaded!"),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to download CSV", error);
        addToast({
          title: t("Download failed"),
          description: t("Unable to download table as CSV"),
          color: "danger",
        });
      }
    },
    [t],
  );

  // Document-specific export functions
  const handleCopyAsMarkdown = useCallback(
    async (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.DOCUMENT) {
          throw new Error("Artefact is not a document");
        }

        // For documents, the content is already in markdown format
        await navigator.clipboard.writeText(artefact.content);

        addToast({
          title: t("Markdown copied!"),
          description: t("Document copied as Markdown format"),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to copy as Markdown", error);
        addToast({
          title: t("Copy failed"),
          description: t("Unable to copy document as Markdown"),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleDownloadAsMarkdown = useCallback(
    (artefact: Artefact) => {
      try {
        let markdownContent: string;

        if (artefact.type === ArtefactType.TABLE) {
          // Convert table data to Markdown table format
          const tableData = parseTableContent(artefact.content);

          if (!tableData) {
            throw new Error("Unable to parse table content");
          }

          // Add title as heading and then the table
          markdownContent = `# ${artefact.title}\n\n${tableToMarkdown(tableData.headers, tableData.rows)}`;
        } else if (artefact.type === ArtefactType.DOCUMENT || artefact.type === ArtefactType.CODE) {
          // For documents and code, use content directly
          markdownContent = artefact.content;
        } else {
          throw new Error("Artefact type not supported for Markdown export");
        }

        // Generate smart filename based on content
        const baseFilename = artefact.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
        const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
        const filename = `${baseFilename}_${timestamp}.md`;

        const blob = new Blob([markdownContent], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        addToast({
          title: t("Markdown downloaded!"),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to download Markdown", error);
        addToast({
          title: t("Download failed"),
          description: t("Unable to download as Markdown"),
          color: "danger",
        });
      }
    },
    [t],
  );

  // Enhanced multi-format export functions
  const handleDownloadAsPDFVisual = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Exporting to {{format}}...", { format: "PDF" }),
        description: t("Please wait while we prepare your download"),
        color: "default",
      });

      try {
        // For HTML artefacts, capture the iframe
        if (artefact.type === ArtefactType.HTML) {
          const iframe = document.querySelector(
            `iframe[data-artefact-id="${artefact.id}"]`,
          ) as HTMLIFrameElement;

          if (!iframe || !iframe.contentDocument) {
            throw new Error("Iframe not found for artefact");
          }

          const element = iframe.contentDocument.body;

          // Use html2pdf.js for high-quality PDF generation
          const opt = {
            margin: 10,
            filename: generateFilename(artefact, "pdf"),
            image: { type: "jpeg" as const, quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              logging: false,
              onclone: (clonedDoc: globalThis.Document) => {
                sanitizeUnsupportedColors(clonedDoc);
              },
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" as const },
            // Prevent word/element splitting across page boundaries
            pagebreak: {
              mode: ["avoid-all", "css", "legacy"] as const,
              avoid: ["p", "li", "h1", "h2", "h3", "h4", "h5", "h6", "tr", "blockquote", "pre"],
            },
          };

          await html2pdf().set(opt).from(element).save();
        } else if (artefact.type === ArtefactType.TABLE) {
          // For TABLE artefacts, capture the rendered table container
          const escapedId = escapeCssSelector(artefact.id);
          const tableContainer = document.querySelector(
            `[data-artefact-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!tableContainer) {
            throw new Error("Table container not found for artefact");
          }

          // Use html2pdf.js for high-quality PDF generation with table preserved
          const opt = {
            margin: 10,
            filename: generateFilename(artefact, "pdf"),
            image: { type: "jpeg" as const, quality: 0.98 },
            html2canvas: {
              scale: 3,
              backgroundColor: "#ffffff",
              useCORS: true,
              logging: false,
              allowTaint: false,
              removeContainer: true,
              imageTimeout: 15000,
              onclone: (clonedDoc: globalThis.Document) => {
                sanitizeUnsupportedColors(clonedDoc);

                // Find and remove max-height, overflow, and width constraints from the cloned element
                const clonedContainer = clonedDoc.querySelector(
                  `[data-artefact-id="${escapedId}"]`,
                ) as HTMLElement;

                if (clonedContainer) {
                  // Remove all constraints from the container and its children
                  const allElements = clonedContainer.querySelectorAll("*");

                  allElements.forEach((el) => {
                    const element = el as HTMLElement;

                    // Remove height constraints
                    element.style.maxHeight = "none";
                    element.style.height = "auto";

                    // Remove overflow constraints
                    element.style.overflow = "visible";
                    element.style.overflowX = "visible";
                    element.style.overflowY = "visible";

                    // Remove width constraints that truncate content
                    if (element.classList.contains("truncate")) {
                      element.style.maxWidth = "none";
                      element.style.overflow = "visible";
                      element.style.textOverflow = "clip";
                      element.style.whiteSpace = "normal";
                    }

                    // Ensure table cells show full content
                    if (element.tagName === "TD" || element.tagName === "TH") {
                      element.style.maxWidth = "none";
                      element.style.whiteSpace = "nowrap";
                    }
                  });

                  // Update the container itself
                  clonedContainer.style.maxHeight = "none";
                  clonedContainer.style.height = "auto";
                  clonedContainer.style.overflow = "visible";
                  clonedContainer.style.width = "auto";
                  clonedContainer.style.minWidth = "fit-content";

                  // Add vertical borders to table cells for PDF readability
                  const tableCells = clonedContainer.querySelectorAll("td, th");

                  tableCells.forEach((cell) => {
                    const cellEl = cell as HTMLElement;

                    // Apply light gray border (default-200) to right side
                    cellEl.style.borderRight = "1px solid #E4E4E7";

                    // Remove right border from the last cell in each row
                    if (!cellEl.nextElementSibling) {
                      cellEl.style.borderRight = "none";
                    }
                  });
                }
              },
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" as const },
            // Prevent table rows from being split across page boundaries
            pagebreak: {
              mode: ["avoid-all", "css", "legacy"] as const,
              avoid: ["tr", "thead", "tbody", "th", "td"],
            },
          };

          await html2pdf().set(opt).from(tableContainer).save();
        } else if (artefact.type === ArtefactType.CODE) {
          // For CODE artefacts, capture the syntax-highlighted DOM element
          const escapedId = escapeCssSelector(artefact.id);
          const codeElement = document.querySelector(
            `[data-artefact-id="${escapedId}"] .syntax-highlighter`,
          ) as HTMLElement;

          if (!codeElement) {
            throw new Error("Code element not found for artefact");
          }

          // Use html2pdf.js for high-quality PDF generation with syntax highlighting preserved
          const opt = {
            margin: 10,
            filename: generateFilename(artefact, "pdf"),
            image: { type: "jpeg" as const, quality: 0.98 },
            html2canvas: {
              scale: 3, // High DPI for crisp code text
              backgroundColor: "#ffffff",
              useCORS: true,
              logging: false,
              allowTaint: false,
              removeContainer: true,
              imageTimeout: 15000,
              onclone: (clonedDoc: globalThis.Document) => {
                sanitizeUnsupportedColors(clonedDoc);
              },
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" as const },
            // Prevent code blocks from being split across page boundaries
            pagebreak: {
              mode: ["avoid-all", "css", "legacy"] as const,
              avoid: ["pre", "code", ".syntax-highlighter"],
            },
          };

          await html2pdf().set(opt).from(codeElement).save();
        } else {
          // For other types, use text-based PDF
          await handleDownloadAsPDFText(artefact);

          return;
        }

        // Show success toast
        const filename = generateFilename(artefact, "pdf");

        addToast({
          title: t("{{format}} exported successfully", { format: "PDF" }),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to export PDF (visual)", error);
        addToast({
          title: t("Failed to export {{format}}", { format: "PDF" }),
          description: t("Unable to export content as PDF"),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleDownloadAsPDFText = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Exporting to {{format}}...", { format: "PDF" }),
        description: t("Creating selectable text PDF"),
        color: "default",
      });

      try {
        // Create PDF with production-quality markdown rendering
        // Don't add artefact title as heading - the content typically contains
        // the title already, and adding it creates duplicate headings in exports
        const markdown = artefact.content;
        const doc = await renderMarkdownToPdf(markdown, {
          title: artefact.title,
          author: "Invest AI",
          brandFontFamily: brand.fontFamily,
          brandCodeFontFamily: brand.codeFontFamily,
        });

        // Save the PDF
        const filename = generateFilename(artefact, "pdf");

        doc.save(filename);

        // Show success toast
        addToast({
          title: t("{{format}} exported successfully", { format: "PDF" }),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to export PDF (text)", error);
        addToast({
          title: t("Failed to export {{format}}", { format: "PDF" }),
          description: t("Unable to export content as PDF"),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleDownloadAsWord = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Exporting to {{format}}...", { format: "Word" }),
        description: t("Please wait while we prepare your document"),
        color: "default",
      });

      try {
        // Create production-quality DOCX with full markdown formatting
        // Use smart title detection to avoid duplicate titles
        // Use export font preference (user-selected or brand default)
        const markdown = prepareMarkdownWithTitle(artefact.content, artefact.title);
        const blob = await convertMarkdownToDocx(markdown, {
          title: artefact.title,
          creator: "Invest AI",
          fontFamily: getExportFontFamily(),
          codeFontFamily: brand.codeFontFamily,
          pageSize: exportPageSize,
        });

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const filename = generateFilename(artefact, "docx");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Show success toast
        addToast({
          title: t("{{format}} exported successfully", { format: "Word" }),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to export Word", error);
        addToast({
          title: t("Failed to export {{format}}", { format: "Word" }),
          description: t("Unable to export content as Word document"),
          color: "danger",
        });
      }
    },
    [t, brand.codeFontFamily, exportPageSize, getExportFontFamily],
  );

  const handleDownloadAsWordVisual = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Exporting to {{format}}...", { format: "Word" }),
        description: t("Creating visual document"),
        color: "default",
      });

      try {
        let canvas: HTMLCanvasElement;

        // Handle HTML artefacts (iframe-based)
        if (artefact.type === ArtefactType.HTML) {
          const iframe = document.querySelector(
            `iframe[data-artefact-id="${artefact.id}"]`,
          ) as HTMLIFrameElement;

          if (!iframe || !iframe.contentDocument) {
            throw new Error("Iframe not found for artefact");
          }

          canvas = await html2canvas(iframe.contentDocument.body, {
            scale: 3,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            allowTaint: false,
            removeContainer: true,
            imageTimeout: 15000,
            windowWidth: iframe.contentDocument.body.scrollWidth,
            windowHeight: iframe.contentDocument.body.scrollHeight,
            // Sanitize unsupported CSS colour functions before capture
            onclone: (clonedDoc: globalThis.Document) => {
              sanitizeUnsupportedColors(clonedDoc);
            },
          });
        } else if (artefact.type === ArtefactType.TABLE) {
          // Handle TABLE artefacts (DOM-based) - capture the whole container
          const escapedId = escapeCssSelector(artefact.id);
          const tableContainer = document.querySelector(
            `[data-artefact-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!tableContainer) {
            throw new Error("Table container not found for artefact");
          }

          canvas = await html2canvas(tableContainer, {
            scale: 3,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            allowTaint: false,
            removeContainer: true,
            imageTimeout: 15000,
            // Sanitize unsupported CSS colour functions before capture
            onclone: (clonedDoc: globalThis.Document) => {
              sanitizeUnsupportedColors(clonedDoc);
            },
          });
        } else {
          throw new Error("Visual export not supported for this artefact type");
        }

        // 2. Convert canvas to Uint8Array for docx ImageRun
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((b) => resolve(b!), "image/png");
        });
        const arrayBuffer = await blob.arrayBuffer();
        const imageData = new Uint8Array(arrayBuffer);

        // 3. Calculate dimensions (A4 width = 600px, maintain aspect ratio)
        const maxWidth = 600;
        const imageHeight = Math.round(canvas.height * (maxWidth / canvas.width));

        // 4. Create Word document with embedded image
        const doc = new Document({
          sections: [
            {
              properties: {},
              children: [
                new Paragraph({
                  text: artefact.title,
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  text: "", // Spacing
                }),
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: imageData,
                      transformation: {
                        width: maxWidth,
                        height: imageHeight,
                      },
                      type: "png",
                    }),
                  ],
                }),
              ],
            },
          ],
        });

        // 5. Generate and download
        const docBlob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(docBlob);
        const link = document.createElement("a");
        const filename = generateFilename(artefact, "docx");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Success toast
        addToast({
          title: t("{{format}} exported successfully", { format: "Word" }),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to export Word (visual)", error);
        addToast({
          title: t("Failed to export {{format}}", { format: "Word" }),
          description: t("Unable to export visual Word document"),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleDownloadAsPowerPoint = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Exporting to {{format}}...", { format: "PowerPoint" }),
        description: t("Creating PowerPoint presentation"),
        color: "default",
      });

      try {
        // Convert markdown to PowerPoint using utility
        // Use export font preference (user-selected or brand default)
        const pptx = await convertMarkdownToPptxSlides(artefact.content, {
          title: artefact.title,
          fontFamily: getExportFontFamily(),
          codeFontFamily: brand.codeFontFamily,
        });

        // Save file
        const filename = generateFilename(artefact, "pptx");

        pptx.writeFile({ fileName: filename });

        // Show success toast
        addToast({
          title: t("{{format}} exported successfully", { format: "PowerPoint" }),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to export PowerPoint", error);
        addToast({
          title: t("Failed to export {{format}}", { format: "PowerPoint" }),
          description: t("Unable to export content as PowerPoint"),
          color: "danger",
        });
      }
    },
    [t, brand.codeFontFamily, getExportFontFamily],
  );

  const handleDownloadAsPowerPointTable = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Exporting to {{format}}...", { format: "PowerPoint" }),
        description: t("Creating table presentation"),
        color: "default",
      });

      try {
        if (artefact.type !== ArtefactType.TABLE) {
          throw new Error("Artefact is not a table");
        }

        // Parse table content using existing utility
        const tableData = parseTableContent(artefact.content);

        if (!tableData) {
          throw new Error("Failed to parse table data");
        }

        // Get font family from user preference or brand
        const fontFamily = getExportFontFamily();

        // Create PowerPoint with table
        const pptx = new PptxGenJS();

        // Title slide
        const titleSlide = pptx.addSlide();

        titleSlide.addText(artefact.title, {
          x: 0.5,
          y: 1.0,
          w: 9,
          h: 1,
          fontSize: 32,
          bold: true,
          align: "center",
          fontFace: fontFamily,
        });

        // Table slide
        const tableSlide = pptx.addSlide();
        const rows = [
          tableData.headers,
          ...tableData.rows.map((row) => tableData.headers.map((h) => (row[h] || "").toString())),
        ];

        tableSlide.addTable(rows, {
          x: 0.5,
          y: 1.0,
          w: 9,
          colW: rows[0].map(() => 9 / rows[0].length),
          border: { pt: 1, color: "CCCCCC" },
          fill: { color: "F7F7F7" },
          fontSize: 10,
          fontFace: fontFamily,
        });

        // Save
        const filename = generateFilename(artefact, "pptx");

        pptx.writeFile({ fileName: filename });

        // Show success toast
        addToast({
          title: t("{{format}} exported successfully", { format: "PowerPoint" }),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to export PowerPoint table", error);
        addToast({
          title: t("Failed to export {{format}}", { format: "PowerPoint" }),
          description: t("Unable to export table as PowerPoint"),
          color: "danger",
        });
      }
    },
    [t, getExportFontFamily],
  );

  const handleDownloadAsPowerPointVisual = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Exporting to {{format}}...", { format: "PowerPoint" }),
        description: t("Capturing visual content"),
        color: "default",
      });

      try {
        let canvas: HTMLCanvasElement;

        // Handle HTML artefacts (iframe-based)
        if (artefact.type === ArtefactType.HTML) {
          const iframe = document.querySelector(
            `iframe[data-artefact-id="${artefact.id}"]`,
          ) as HTMLIFrameElement;

          if (!iframe || !iframe.contentDocument) {
            throw new Error("Iframe not found for artefact");
          }

          canvas = await html2canvas(iframe.contentDocument.body, {
            scale: 3,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            allowTaint: false,
            removeContainer: true,
            imageTimeout: 15000,
            windowWidth: iframe.contentDocument.body.scrollWidth,
            windowHeight: iframe.contentDocument.body.scrollHeight,
            // Sanitize unsupported CSS colour functions before capture
            onclone: (clonedDoc: globalThis.Document) => {
              sanitizeUnsupportedColors(clonedDoc);
            },
          });
        } else if (artefact.type === ArtefactType.TABLE) {
          // Handle TABLE artefacts (DOM-based) - capture the whole container
          const escapedId = escapeCssSelector(artefact.id);
          const tableContainer = document.querySelector(
            `[data-artefact-id="${escapedId}"]`,
          ) as HTMLElement;

          if (!tableContainer) {
            throw new Error("Table container not found for artefact");
          }

          canvas = await html2canvas(tableContainer, {
            scale: 3,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            allowTaint: false,
            removeContainer: true,
            imageTimeout: 15000,
            // Sanitize unsupported CSS colour functions before capture
            onclone: (clonedDoc: globalThis.Document) => {
              sanitizeUnsupportedColors(clonedDoc);
            },
          });
        } else {
          throw new Error("Visual export not supported for this artefact type");
        }

        // Convert to base64
        const base64Image = canvas.toDataURL("image/png");

        // Get font family from user preference or brand
        const fontFamily = getExportFontFamily();

        // Create PowerPoint with image
        const pptx = new PptxGenJS();

        // Title slide
        const titleSlide = pptx.addSlide();

        titleSlide.addText(artefact.title, {
          x: 0.5,
          y: 1.0,
          w: 9,
          h: 1,
          fontSize: 32,
          bold: true,
          align: "center",
          fontFace: fontFamily,
        });

        // Image slide
        const imageSlide = pptx.addSlide();
        const aspectRatio = canvas.height / canvas.width;
        const imageWidth = 9;
        const imageHeight = imageWidth * aspectRatio;

        imageSlide.addImage({
          data: base64Image,
          x: 0.5,
          y: Math.max(0.5, (6.5 - imageHeight) / 2),
          w: imageWidth,
          h: Math.min(imageHeight, 6.5),
        });

        // Save
        const filename = generateFilename(artefact, "pptx");

        pptx.writeFile({ fileName: filename });

        // Show success toast
        addToast({
          title: t("{{format}} exported successfully", { format: "PowerPoint" }),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to export PowerPoint (visual)", error);
        addToast({
          title: t("Failed to export {{format}}", { format: "PowerPoint" }),
          description: t("Unable to export visual PowerPoint"),
          color: "danger",
        });
      }
    },
    [t, getExportFontFamily],
  );

  const handleDownloadAsExcel = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Exporting to {{format}}...", { format: "Excel" }),
        description: t("Preparing export"),
        color: "default",
      });

      try {
        // Use batch export which supports all artefact types:
        // - TABLE: proper Excel tables with formatting
        // - DOCUMENT: markdown stripped to text cells
        // - HTML: captured as images
        // - CODE: code blocks with monospace font
        await batchExportToExcel(
          [artefact],
          {
            useSeparateWorksheets: false,
            fontFamily: getExportFontFamily(),
            codeFontFamily: brand.codeFontFamily,
          },
          artefact.title,
          brand,
          theme,
        );

        // Show success toast
        const filename = generateFilename(artefact, "xlsx");

        addToast({
          title: t("{{format}} exported successfully", { format: "Excel" }),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to export Excel", error);
        addToast({
          title: t("Failed to export {{format}}", { format: "Excel" }),
          description: t("Unable to export content as Excel"),
          color: "danger",
        });
      }
    },
    [t, brand, theme, getExportFontFamily],
  );

  const handleDownloadAsInteractiveHTML = useCallback(
    async (artefact: Artefact) => {
      // Show loading toast
      addToast({
        title: t("Exporting to {{format}}...", { format: "Interactive HTML" }),
        description: t("Creating standalone HTML file"),
        color: "default",
      });

      try {
        if (artefact.type !== ArtefactType.HTML) {
          throw new Error("Artefact is not HTML");
        }

        // Create standalone HTML with metadata
        const timestamp = new Date().toLocaleString();
        const standaloneHTML = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${artefact.title}</title>
    <meta name="generator" content="Pathway AI Platform">
    <meta name="created" content="${timestamp}">
    <style>
        body {
            font-family: system-ui, -apple-system, sans-serif;
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }
        h1 {
            color: #333;
            border-bottom: 2px solid #0070f3;
            padding-bottom: 10px;
        }
        .metadata {
            color: #666;
            font-size: 0.9em;
            margin-bottom: 20px;
        }
    </style>
    <!-- Chart.js CDN (if needed) -->
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <!-- Plotly CDN (if needed) -->
    <script src="https://cdn.plot.ly/plotly-latest.min.js"></script>
</head>
<body>
    <h1>${artefact.title}</h1>
    <div class="metadata">
        Generated: ${timestamp} | Source: Pathway AI Platform
    </div>
    ${artefact.content}
</body>
</html>`;

        // Create download
        const blob = new Blob([standaloneHTML], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const filename = generateFilename(artefact, "html");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        // Show success toast
        addToast({
          title: t("{{format}} exported successfully", { format: "Interactive HTML" }),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to export Interactive HTML", error);
        addToast({
          title: t("Failed to export {{format}}", { format: "Interactive HTML" }),
          description: t("Unable to export content as Interactive HTML"),
          color: "danger",
        });
      }
    },
    [t],
  );

  const handleDownloadAsHTMLSource = useCallback(
    async (artefact: Artefact) => {
      try {
        if (artefact.type !== ArtefactType.HTML) {
          throw new Error("Artefact is not HTML");
        }

        const blob = new Blob([artefact.content], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const filename = generateFilename(artefact, "html");

        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        addToast({
          title: t("HTML source downloaded!"),
          description: t("Saved as {{filename}}", { filename }),
          color: "success",
        });
      } catch (error) {
        logger.error("❌ Failed to download HTML source", error);
        addToast({
          title: t("Download failed"),
          description: t("Unable to download HTML source"),
          color: "danger",
        });
      }
    },
    [t],
  );

  // Unified download handler with format support
  const handleDownloadWithFormat = useCallback(
    async (artefact: Artefact, format: DownloadFormat) => {
      switch (format) {
        case "png":
          return handleDownloadAsImage(artefact);
        case "pdf-visual":
          return handleDownloadAsPDFVisual(artefact);
        case "pdf-text":
          return handleDownloadAsPDFText(artefact);
        case "excel":
          return handleDownloadAsExcel(artefact);
        case "csv":
          return handleDownloadAsCSV(artefact);
        case "word":
          return handleDownloadAsWord(artefact);
        case "word-visual":
          return handleDownloadAsWordVisual(artefact);
        case "powerpoint":
          // For tables, use data-based PowerPoint export with table
          if (artefact.type === ArtefactType.TABLE) {
            return handleDownloadAsPowerPointTable(artefact);
          }

          return handleDownloadAsPowerPoint(artefact);
        case "powerpoint-visual":
          return handleDownloadAsPowerPointVisual(artefact);
        case "html-source":
          return handleDownloadAsHTMLSource(artefact);
        case "html-interactive":
          return handleDownloadAsInteractiveHTML(artefact);
        case "markdown":
          return handleDownloadAsMarkdown(artefact);
        case "text":
          return handleDownload(artefact);
        default:
          return handleDownload(artefact);
      }
    },
    [
      handleDownload,
      handleDownloadAsImage,
      handleDownloadAsPDFVisual,
      handleDownloadAsPDFText,
      handleDownloadAsExcel,
      handleDownloadAsCSV,
      handleDownloadAsWord,
      handleDownloadAsWordVisual,
      handleDownloadAsPowerPoint,
      handleDownloadAsPowerPointTable,
      handleDownloadAsPowerPointVisual,
      handleDownloadAsHTMLSource,
      handleDownloadAsInteractiveHTML,
      handleDownloadAsMarkdown,
    ],
  );

  return {
    handleCopy,
    handleCopyPlain,
    handleCopyWithFilename,
    handleCopyWithMetadata,
    handleDownload,
    handleRefresh,
    formatTimestamp,
    // Table-specific exports
    handleCopyAsCSV,
    handleCopyAsJSON,
    handleDownloadAsCSV,
    // Document-specific exports
    handleCopyAsMarkdown,
    handleDownloadAsMarkdown,
    // HTML-specific exports
    handleCopyAsImage,
    handleDownloadAsImage,
    // Enhanced multi-format exports
    handleDownloadAsPDFVisual,
    handleDownloadAsPDFText,
    handleDownloadAsWord,
    handleDownloadAsWordVisual,
    handleDownloadAsPowerPoint,
    handleDownloadAsPowerPointTable,
    handleDownloadAsPowerPointVisual,
    handleDownloadAsExcel,
    handleDownloadAsInteractiveHTML,
    handleDownloadAsHTMLSource,
    handleDownloadWithFormat,
  };
};
